// Appels API « org » (companies / groupes / invitations / souscriptions / catalogue),
// via apiFetch (Bearer + intercepteur 401). Tout est scopé côté serveur à l'email du token.
import { apiFetch } from './client';

const A = (token: string | null) => ({ token, auth: true as const });
const POST = (token: string | null, body?: unknown) => ({ token, auth: true as const, method: 'POST', body });
const PUT = (token: string | null, body?: unknown) => ({ token, auth: true as const, method: 'PUT', body });
const DEL = (token: string | null) => ({ token, auth: true as const, method: 'DELETE' });

export interface OrgCompany { companyid: number; name: string; slug: string; tenant_id: number; roles: string[]; status?: string; }
export interface LicenseUsage { licenses: number; used: number; available: number; subscription_active: boolean; plan?: string | null; }
export interface LicenseCart { quantity: number; unit_price: number; total: number; currency?: string; }
export interface OrgSummary {
  email: string;
  companies: OrgCompany[];
  roles: string[];
  isCompanyAdmin: boolean;
  isAuthor: boolean;
  capabilities: { manageCompany: boolean; boAdminLink: boolean; studioLink: boolean };
}
export interface OrgGroup { group_id: number; name: string; description?: string | null; role_count?: number; member_count?: number; role_ids?: number[]; member_ids?: number[]; mission_ids?: number[]; }
export interface OrgMember { app_user_id: number; member_id?: number | null; email: string; firstname?: string | null; lastname?: string | null; roles: string[]; status?: string; is_admin?: boolean;
  role_ids?: number[];
}
export interface OrgRole { roleid: number; key: string; name: string; scope: string; }
export interface OrgInvitation { invitation_id: number; email?: string; company_id?: number; company_name?: string; token?: string; status: string; created_at?: string; }
export interface OrgCompanyMission { missionid: number; title: string; subtitle?: string; difficulty?: string; category?: string; coverimagekey?: string | null; company_id?: number; company_name?: string; assignment_status?: string | null; is_trial?: number | null; }

export const orgApi = {
  myOrg: (t: string | null) => apiFetch<OrgSummary>('/me/org', A(t)),
  myCompanies: (t: string | null) => apiFetch<{ companies: OrgCompany[] }>('/me/companies', A(t)),
  createCompany: (t: string | null, body: { name: string; slug?: string; description?: string; domain?: string }) =>
    apiFetch('/me/companies', POST(t, body)),
  archiveCompany: (t: string | null, cid: number) => apiFetch(`/companies/${cid}/archive`, POST(t)),
  reactivateCompany: (t: string | null, cid: number) => apiFetch(`/companies/${cid}/reactivate`, POST(t)),
  deleteCompany: (t: string | null, cid: number) => apiFetch(`/companies/${cid}/delete`, POST(t)),

  groups: (t: string | null, cid: number) => apiFetch<{ groups: OrgGroup[] }>(`/companies/${cid}/groups`, A(t)),
  group: (t: string | null, cid: number, gid: number) => apiFetch<OrgGroup>(`/companies/${cid}/groups/${gid}`, A(t)),
  createGroup: (t: string | null, cid: number, body: { name: string; description?: string }) =>
    apiFetch(`/companies/${cid}/groups`, POST(t, body)),
  updateGroup: (t: string | null, cid: number, gid: number, body: { name: string; description?: string }) =>
    apiFetch(`/companies/${cid}/groups/${gid}`, PUT(t, body)),
  deleteGroup: (t: string | null, cid: number, gid: number) => apiFetch(`/companies/${cid}/groups/${gid}`, DEL(t)),
  setGroupRoles: (t: string | null, cid: number, gid: number, role_ids: number[]) =>
    apiFetch(`/companies/${cid}/groups/${gid}/roles`, PUT(t, { role_ids })),
  setGroupUsers: (t: string | null, cid: number, gid: number, app_user_ids: number[]) =>
    apiFetch(`/companies/${cid}/groups/${gid}/users`, PUT(t, { app_user_ids })),
  setGroupMissions: (t: string | null, cid: number, gid: number, mission_ids: number[]) =>
    apiFetch(`/companies/${cid}/groups/${gid}/missions`, PUT(t, { mission_ids })),

  members: (t: string | null, cid: number) =>
    apiFetch<{ members: OrgMember[]; usage: LicenseUsage }>(`/companies/${cid}/members`, A(t)),
  activateMember: (t: string | null, cid: number, mid: number) =>
    apiFetch<{ member_id: number; status: string }>(`/companies/${cid}/members/${mid}/activate`, POST(t)),
  deactivateMember: (t: string | null, cid: number, mid: number) =>
    apiFetch<{ member_id: number; status: string }>(`/companies/${cid}/members/${mid}/deactivate`, POST(t)),

  // Roles assigned DIRECTLY to a member, without going through a group. The server applies them
  // at once for an active member, and at activation otherwise.
  setMemberRoles: (t: string | null, cid: number, mid: number, role_ids: number[]) =>
    apiFetch<{ member_id: number; role_ids: number[]; applied: boolean }>(
      `/companies/${cid}/members/${mid}/roles`, { ...POST(t), method: 'PUT', body: { role_ids } }),
  roles: (t: string | null, cid: number) => apiFetch<{ roles: OrgRole[] }>(`/companies/${cid}/roles`, A(t)),

  invite: (t: string | null, cid: number, body: { email: string; role_ids?: number[]; group_ids?: number[]; requires_acceptance?: boolean }) =>
    apiFetch(`/companies/${cid}/invitations`, POST(t, body)),
  companyInvitations: (t: string | null, cid: number) =>
    apiFetch<{ invitations: OrgInvitation[] }>(`/companies/${cid}/invitations`, A(t)),
  cancelInvitation: (t: string | null, cid: number, iid: number) =>
    apiFetch(`/companies/${cid}/invitations/${iid}`, DEL(t)),
  resendInvitation: (t: string | null, cid: number, iid: number) =>
    apiFetch(`/companies/${cid}/invitations/${iid}/resend`, POST(t)),
  myInvitations: (t: string | null) => apiFetch<{ invitations: OrgInvitation[] }>('/me/invitations', A(t)),
  invitationByToken: (t: string | null, token: string) =>
    apiFetch<{ company_name: string; status: string; expired: boolean; matches_me: boolean }>(
      `/me/invitations/by-token/${encodeURIComponent(token)}`, A(t)),
  acceptInvitation: (t: string | null, token: string) => apiFetch(`/me/invitations/${token}/accept`, POST(t)),
  declineInvitation: (t: string | null, token: string) => apiFetch(`/me/invitations/${token}/decline`, POST(t)),

  subscription: (t: string | null, cid: number) =>
    apiFetch<{ subscription: any; usage: LicenseUsage }>(`/companies/${cid}/subscription`, A(t)),
  setSubscription: (t: string | null, cid: number, body: { plan: string; licenses: number }) =>
    apiFetch(`/companies/${cid}/subscription`, POST(t, body)),

  // Achat de licences (panier + commande, réutilise le checkout).
  licenseCart: (t: string | null, cid: number) =>
    apiFetch<LicenseCart>(`/companies/${cid}/license-cart`, A(t)),
  setLicenseCart: (t: string | null, cid: number, quantity: number) =>
    apiFetch<LicenseCart>(`/companies/${cid}/license-cart`, PUT(t, { quantity })),
  checkoutLicenses: (t: string | null, cid: number) =>
    apiFetch<{ status?: string; checkout_url?: string; session_id?: string; usage?: LicenseUsage; free?: boolean; simulated?: boolean }>(
      `/companies/${cid}/license-cart/checkout`, POST(t)),
  confirmLicenses: (t: string | null, cid: number, session_id: string) =>
    apiFetch<{ status: string; usage?: LicenseUsage }>(`/companies/${cid}/license-cart/checkout/confirm`, POST(t, { session_id })),

  catalog: (t: string | null, cid: number) =>
    apiFetch<{ missions: OrgCompanyMission[] }>(`/companies/${cid}/missions`, A(t)),
  setCatalog: (t: string | null, cid: number, mission_ids: number[]) =>
    apiFetch(`/companies/${cid}/missions`, PUT(t, { mission_ids })),

  myCompanyMissions: (t: string | null) =>
    apiFetch<{ missions: OrgCompanyMission[] }>('/me/company-missions', A(t)),
};
